import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, XCircle, Loader2, Package, Store, Clock,
  MapPin, Receipt,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * CheckoutSuccessPage — polished order confirmation.
 * Polls `/api/checkout/status/{session_id}` until Stripe confirms the
 * payment, then swaps to a success hero with the pickup slot, the
 * business, an itemised summary, and clear "View Order · Continue
 * Shopping · View Business" CTAs.
 */
const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking");
  const [payment, setPayment] = useState(null);
  const [order, setOrder] = useState(null);
  const [business, setBusiness] = useState(null);
  const hasPolled = useRef(false);

  const sessionId = searchParams.get("session_id");

  const loadOrderDetail = useCallback(async (orderId) => {
    if (!orderId) return;
    try {
      const r = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
      setOrder(r.data);
      const bizId = r.data?.items?.[0]?.business_id;
      if (bizId) {
        const b = await axios.get(`${API}/businesses/${bizId}`);
        setBusiness(b.data);
      }
    } catch {
      /* order details are optional for the success hero */
    }
  }, []);

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    if (attempts >= MAX_POLLS) { setStatus("timeout"); return; }
    try {
      const r = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
      setPayment(r.data);
      if (r.data?.payment_status === "paid") {
        setStatus("success");
        if (r.data?.order_id) loadOrderDetail(r.data.order_id);
        return;
      }
      if (r.data?.status === "expired") { setStatus("expired"); return; }
      setStatus("processing");
      setTimeout(() => pollPaymentStatus(attempts + 1), POLL_INTERVAL_MS);
    } catch {
      setStatus("error");
    }
  }, [sessionId, loadOrderDetail]);

  useEffect(() => {
    if (sessionId && user && !hasPolled.current) {
      hasPolled.current = true;
      pollPaymentStatus();
    }
  }, [sessionId, user, pollPaymentStatus]);

  const paidTotal = useMemo(() => {
    if (payment?.amount_total != null) return Number(payment.amount_total) / 100;
    if (order?.total != null) return Number(order.total);
    return null;
  }, [payment, order]);

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-success-page">
      <Navbar />

      <section className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {status === "checking" || status === "processing" ? (
            <ProcessingCard />
          ) : status === "success" ? (
            <SuccessBlock order={order} business={business} paidTotal={paidTotal} />
          ) : (
            <ErrorBlock status={status} />
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProcessingCard() {
  return (
    <div className="text-center animate-fadeIn" data-testid="success-processing">
      <Loader2 className="w-16 h-16 text-tbc-gold mx-auto mb-6 animate-spin" />
      <h1 className="font-heading text-3xl text-tbc-navy">Confirming your payment…</h1>
      <p className="mt-3 text-gray-600 font-body">
        This usually takes a few seconds. Please don't close this tab.
      </p>
    </div>
  );
}

function SuccessBlock({ order, business, paidTotal }) {
  const items = order?.items || [];
  const pickupTime = order?.fulfillment?.pickup_time;
  const pickupType = order?.fulfillment?.type || "pickup";
  const customer = order?.customer_details || {};
  const readableTime = pickupTime ? formatPickup(pickupTime) : null;

  return (
    <div className="animate-fadeIn" data-testid="success-block">
      <div className="text-center">
        <div
          className="mx-auto w-20 h-20 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center"
          aria-hidden="true"
        >
          <CheckCircle2 className="w-11 h-11 text-emerald-600" strokeWidth={1.8} />
        </div>
        <p className="mt-5 text-xs uppercase tracking-[0.28em] text-emerald-700 font-semibold">
          Order Confirmed
        </p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl text-tbc-navy" data-testid="success-title">
          Thank you for supporting a Black-owned business.
        </h1>
        {order?.order_id && (
          <p className="mt-3 text-sm text-gray-500 font-body" data-testid="success-order-id">
            Order <span className="font-mono text-tbc-navy">#{order.order_id.replace(/^order_/, "")}</span>
            {paidTotal != null && (
              <span> · <span className="text-tbc-navy font-semibold">${paidTotal.toFixed(2)}</span> charged</span>
            )}
          </p>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {business && (
          <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid="success-business">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-tbc-navy/60 font-body">
              <Store className="w-3.5 h-3.5 text-tbc-gold" /> Sold by
            </div>
            <p className="mt-1.5 font-heading text-tbc-navy">{business.name}</p>
            {(business.city || business.state) && (
              <p className="mt-0.5 text-xs text-gray-500 font-body inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {[business.city, business.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        )}

        {readableTime && (
          <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid="success-pickup">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-tbc-navy/60 font-body">
              <Clock className="w-3.5 h-3.5 text-tbc-gold" /> {pickupType === "delivery" ? "Delivery" : "Pickup"} time
            </div>
            <p className="mt-1.5 font-heading text-tbc-navy">{readableTime}</p>
            {customer.name && (
              <p className="mt-0.5 text-xs text-gray-500 font-body">
                For {customer.name}{customer.phone ? ` · ${customer.phone}` : ""}
              </p>
            )}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-6 rounded-2xl bg-tbc-cream p-5" data-testid="success-summary">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-tbc-navy/60 font-body mb-3">
            <Receipt className="w-3.5 h-3.5 text-tbc-gold" /> Order summary
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={`${it.product_id}-${i}`} className="flex gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex-shrink-0">
                  <img
                    src={resolveAssetUrl(it.image_url) || ""}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm text-tbc-navy truncate">{it.product_name}</p>
                  <p className="text-[11px] text-gray-500">Qty {it.quantity}</p>
                </div>
                <p className="font-heading text-sm text-tbc-navy tabular-nums">
                  ${Number(it.subtotal || 0).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-tbc-gold/20 space-y-1 text-sm font-body">
            <SummaryRow label="Subtotal" value={order?.subtotal} />
            {Number(order?.tip_amount || 0) > 0 && <SummaryRow label="Tip" value={order.tip_amount} />}
            {Number(order?.points_discount_usd || 0) > 0 && (
              <SummaryRow label={`Points (${order.points_redeemed})`} value={-Number(order.points_discount_usd)} />
            )}
            <div className="flex justify-between font-heading text-tbc-navy text-base pt-1">
              <span>Total</span>
              <span className="tabular-nums">${Number(order?.total || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link to="/orders">
          <Button
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6 py-5"
            data-testid="success-view-order"
          >
            <Package className="w-4 h-4 mr-2" /> View Order
          </Button>
        </Link>
        <Link to="/directory">
          <Button
            variant="outline"
            className="border-2 border-tbc-navy text-tbc-navy hover:bg-tbc-navy hover:text-white rounded-full px-6 py-5"
            data-testid="success-continue-shopping"
          >
            Continue Shopping
          </Button>
        </Link>
        {business && (
          <Link to={`/business/${business.business_id}`}>
            <Button
              variant="ghost"
              className="text-tbc-navy hover:text-tbc-gold rounded-full px-6 py-5"
              data-testid="success-view-business"
            >
              View Business
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between text-gray-600">
      <span>{label}</span>
      <span className="tabular-nums">
        {(Number(value) < 0 ? "-$" : "$") + Math.abs(Number(value || 0)).toFixed(2)}
      </span>
    </div>
  );
}

function formatPickup(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return null; }
}

function ErrorBlock({ status }) {
  return (
    <div className="text-center animate-fadeIn" data-testid="success-error-block">
      <div className="w-20 h-20 mx-auto rounded-full bg-red-100 border border-red-200 flex items-center justify-center">
        <XCircle className="w-11 h-11 text-red-600" />
      </div>
      <h1 className="mt-5 font-heading text-3xl text-tbc-navy">
        {status === "expired" ? "Checkout expired" : "We couldn't confirm your payment"}
      </h1>
      <p className="mt-3 text-gray-600 font-body">
        {status === "expired"
          ? "Your checkout session timed out before payment was captured. Please try again."
          : "If you were charged, your order will still appear in your order history within a minute."}
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/cart">
          <Button className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6 py-5" data-testid="success-back-to-cart">
            Back to Cart
          </Button>
        </Link>
        <Link to="/orders">
          <Button variant="outline" className="border-2 border-tbc-navy text-tbc-navy hover:bg-tbc-navy hover:text-white rounded-full px-6 py-5">
            View Orders
          </Button>
        </Link>
      </div>
    </div>
  );
}
